import type { DomainItem } from '@/types';
import i18n from '@/i18n/config';

import React from 'react';
import {
  RiEditLine,
  RiDatabaseLine,
  RiFolderZipLine,
  RiRadarLine,
  RiCloudLine,
  RiExternalLinkLine
} from '@remixicon/react';
import { Switch, Card, CardBody, CardHeader, Button, Chip, Link } from '@heroui/react';

import { UserCell } from '@/components/shared/UserCell';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';

interface DomainExpandContentProps {
  domain: DomainItem;
  onToggleDebug?: (checked: boolean) => void;
  onToggleTracking?: (checked: boolean) => void;
  onToggleLiveInteraction?: (checked: boolean) => void;
  onEditStatus?: () => void;
  onEditCdn?: () => void;
  onEditDomain?: () => void;
}

function formatUsageLimit(value: number | null | undefined): string {
  if (value == null) return 'Unlimited';
  return Number(value).toLocaleString();
}

export function DomainExpandContent({
  domain,
  onToggleDebug,
  onToggleTracking,
  onToggleLiveInteraction,
  onEditStatus,
  onEditCdn,
  onEditDomain
}: DomainExpandContentProps) {
  const isVerified = domain.status === 'verified';
  const statusColor = isVerified ? 'success' : 'warning';
  const statusLabel = isVerified ? 'Verified' : 'Pending';

  // CDN information
  const cdnUrl = domain.cdn_url_override || domain.cdn?.cdn_url || domain.cdn_url;
  const cdnTag = domain.cdn?.cdn_tag;
  const cdnVersion = domain.cdn?.version;
  const hasCdnInfo = !!cdnUrl || !!cdnTag || !!cdnVersion;

  // Domain URL
  const domainUrl = domain.domain
    ? domain.domain.startsWith('http')
      ? domain.domain
      : `https://${domain.domain}`
    : null;

  return (
    <div className="grid grid-cols-5 gap-4">
      {/* Left Column - col-span-3 */}
      <div className="col-span-5 space-y-4 md:col-span-3">
        {/* Status Section */}
        <div className="space-y-3 rounded-lg bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium">Status:</span>
              <Chip color={statusColor} size="sm" variant="flat">
                {statusLabel}
              </Chip>
            </div>
            <Button
              size="sm"
              variant="light"
              color="primary"
              startContent={<RiEditLine className="h-4 w-4" />}
              onPress={onEditStatus}
            >{i18n.t('common.edit')}</Button>
          </div>
        </div>

        {/* CDN Information Card */}
        <Card>
          <CardHeader>
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center gap-3">
                <RiCloudLine className="h-4 w-4" />
                <span className="text-default-700 text-sm font-semibold">CDN Information</span>
                {cdnTag ? (
                  <Chip size="sm" variant="flat" color="primary">
                    {cdnTag}
                  </Chip>
                ) : null}
              </div>
              {onEditCdn && (
                <Button
                  size="sm"
                  variant="light"
                  color="primary"
                  startContent={<RiEditLine className="h-4 w-4" />}
                  onPress={onEditCdn}
                >
                  {hasCdnInfo ? 'Edit' : 'Set DNS'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardBody className="space-y-3">
            {hasCdnInfo ? (
              <>
                {cdnUrl && <span className="font-mono text-xs break-all">{cdnUrl}</span>}
                {cdnVersion && (
                  <div className="border-default-200 flex items-center gap-3 border-t pt-3">
                    <span className="font-mono text-sm">{cdnVersion}</span>
                  </div>
                )}
              </>
            ) : (
              <span className="text-default-500 text-sm">Not set</span>
            )}
          </CardBody>
        </Card>

        {/* Audit Information Card */}
        <Card>
          <CardHeader>
            <span className="text-default-700 flex items-center gap-2 text-sm font-semibold">
              <div className="bg-secondary h-2 w-2 rounded-full"></div>
              Audit Information
            </span>
          </CardHeader>
          <CardBody>
            <div className="border-default-200 overflow-hidden rounded-lg border">
              <table className="w-full">
                <thead className="bg-default-100">
                  <tr>
                    <th className="text-default-600 px-3 py-2 text-start text-xs font-medium tracking-wide uppercase">{i18n.t('columns.user')}</th>
                    <th className="text-default-600 px-3 py-2 text-start text-xs font-medium tracking-wide uppercase">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-default-200 divide-y">
                  <tr>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-default-500 text-xs font-medium tracking-wide uppercase">{i18n.t('copy.created_by')}</span>
                        {domain.created_by ? (
                          <UserCell user={domain.created_by} dense />
                        ) : (
                          <span className="text-default-500 text-sm">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-default-500 text-xs font-medium tracking-wide uppercase">{i18n.t('columns.createdAt')}</span>
                        {domain.created_at ? (
                          <DateTimeDisplay dateString={domain.created_at} />
                        ) : (
                          <span className="text-default-500 text-sm">-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                  {domain.updated_at && (
                    <tr>
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-default-500 text-xs font-medium tracking-wide uppercase">
                            Last Updated By
                          </span>
                          {domain.updated_by ? (
                            <UserCell user={domain.updated_by} dense />
                          ) : (
                            <span className="text-default-500 text-sm">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-default-500 text-xs font-medium tracking-wide uppercase">{i18n.t('copy.updated_at')}</span>
                          <DateTimeDisplay dateString={domain.updated_at} />
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Right Column - col-span-2 */}
      <div className="col-span-5 space-y-4 md:col-span-2">
        {/* Domain Link Card */}
        {domainUrl && (
          <Card>
            <CardHeader>
              <span className="text-sm font-medium">Website</span>
            </CardHeader>
            <CardBody>
              <Link
                href={domainUrl}
                isExternal
                showAnchorIcon
                anchorIcon={<RiExternalLinkLine className="h-4 w-4" />}
                className="font-mono text-sm break-all"
              >
                {domain.domain}
              </Link>
            </CardBody>
          </Card>
        )}

        {/* Domain Event Cap card */}
        <Card>
          <CardHeader>
            <div className="flex w-full items-center justify-between">
              <span className="text-sm font-medium">{i18n.t('copy.domain_event_cap')}</span>
              {onEditDomain && (
                <Button
                  size="sm"
                  variant="light"
                  color="primary"
                  startContent={<RiEditLine className="h-4 w-4" />}
                  onPress={onEditDomain}
                >{i18n.t('common.edit')}</Button>
              )}
            </div>
          </CardHeader>
          <CardBody>
            <span className="text-default-600 text-sm" title={domain.token_limit == null ? 'Unlimited events' : 'Max processed events for this domain'}>
              {formatUsageLimit(domain.token_limit)}
            </span>
            <p className="text-default-400 mt-1 text-xs">Max events processed for this domain.</p>
          </CardBody>
        </Card>

        {/* Settings Card */}
        <Card>
          <CardHeader>
            <span className="text-sm font-medium">{i18n.t('nav.settings')}</span>
          </CardHeader>
          <CardBody className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RiDatabaseLine className="text-default-400 h-4 w-4" />
                <span className="text-sm">Debug</span>
              </div>
              <Switch size="sm" isSelected={domain.is_debug} onValueChange={onToggleDebug} />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RiRadarLine className="text-default-400 h-4 w-4" />
                <span className="text-sm">Tracking</span>
              </div>
              <Switch size="sm" isSelected={domain.is_tracking} onValueChange={onToggleTracking} />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RiFolderZipLine className="text-default-400 h-4 w-4" />
                <span className="text-sm">Enable Live Interaction</span>
              </div>
              <Switch
                size="sm"
                isSelected={domain.extra?.enable_live_interaction ?? false}
                onValueChange={onToggleLiveInteraction}
              />
            </div>
          </CardBody>
        </Card>

        {/* Version Info Card */}
        <Card>
          <CardHeader>
            <span className="text-sm font-medium">Version Info</span>
          </CardHeader>
          <CardBody className="space-y-2">
            <div className="flex justify-between">
              <span className="text-default-600 text-sm">CV:</span>
              <span className="font-mono text-sm">{domain.cv || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-default-600 text-sm">CH:</span>
              <span className="font-mono text-sm">{domain.ch || '-'}</span>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
